import { dateLabel, dateTimeLabel } from "../dashboard/local-day.js";
import { temperatureKindLabels, typedLogForms } from "../logging/catalog.js";
import {
  displayRecordValue,
  humanLabel,
  type RecordEntry,
  type RecordRevision,
} from "./records-api.js";

export type RecordNames = ReadonlyMap<string, string>;

const shortTypeNames: Readonly<Record<string, string>> = {
  b2b_supply: "Supply",
  calibration: "Calibration",
  cleaning: "Cleaning",
  complaint: "Complaint",
  cook: "Cooking",
  cooling: "Cooling",
  delivery: "Delivery",
  incident: "Problem",
  maintenance: "Maintenance",
  recall: "Recall",
  reheat: "Reheating",
  sickness: "Sickness",
  temperature: "Temperature",
  traceability: "Traceability",
  transport: "Transport",
};

export function shortTypeName(code: string, title?: string): string {
  const known = shortTypeNames[code];
  if (known) return known;
  const base = (title ?? humanLabel(code))
    .replace(/\s*\(.*$/, "")
    .split(" / ")[0]!
    .trim();
  return base || humanLabel(code);
}

function valuesOf(record: RecordEntry): Record<string, unknown> {
  return record.detail ?? record.payload ?? {};
}

function text(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const line = value.split("\n")[0]?.trim();
  return line ? line : undefined;
}

function temperature(value: unknown): string | undefined {
  return typeof value === "number"
    ? formatValue("value_c", value, "")
    : undefined;
}

function named(names: RecordNames, id: unknown): string | undefined {
  return typeof id === "string" ? names.get(id) : undefined;
}

function join(parts: ReadonlyArray<string | undefined>): string {
  return parts.filter((part): part is string => Boolean(part)).join(" · ");
}

const subjectKeys = [
  "food_description",
  "task_description",
  "affected_food",
  "what_went_wrong",
  "complaint",
  "target_description",
  "product_type",
  "scope",
];

// "Walk-in chiller · 3.2 °C": the subject and the value, not the form name.
export function recordHeadline(
  record: RecordEntry,
  names: RecordNames,
  typeTitle?: string,
): string {
  const values = valuesOf(record);
  const short = shortTypeName(record.record_type_code, typeTitle);
  switch (record.record_type_code) {
    case "temperature":
      return join([
        named(names, values.asset_id) ??
          temperatureKindLabels[String(values.kind)] ??
          short,
        temperature(values.value_c),
      ]);
    case "cook":
    case "reheat":
      return join([
        short,
        text(values.food_description),
        temperature(values.internal_temp_c),
      ]);
    case "delivery": {
      const items = Array.isArray(values.items)
        ? (values.items as Array<Record<string, unknown>>)
        : [];
      const rejected = items.filter((item) => item.accepted === false).length;
      return join([
        named(names, values.supplier_id) ?? short,
        items.length > 1
          ? `${items.length} items`
          : text(items[0]?.food_description),
        rejected > 0 ? `${rejected} rejected` : undefined,
      ]);
    }
    case "cleaning":
      return join([
        short,
        text(values.task_description)?.replace(/^clean\s+/i, ""),
      ]);
    case "sickness":
      return join([short, named(names, values.staff_profile_id)]);
    case "maintenance":
      return join([
        short,
        named(names, values.asset_id) ?? text(values.target_description),
      ]);
    case "calibration":
      return join([short, named(names, values.thermometer_asset_id)]);
    case "traceability":
      return join([
        named(names, values.supplier_id) ?? short,
        text(values.product_type),
      ]);
    case "transport":
      return join([
        short,
        text(values.food_description),
        temperature(values.temp_c),
      ]);
    default: {
      const key = subjectKeys.find((entry) => text(values[entry]));
      return join([short, key ? text(values[key]) : undefined]);
    }
  }
}

// Only a result that needs a look is worth a badge.
export function recordResultBadge(
  record: Pick<RecordEntry, "overall_result" | "status">,
): { label: string; tone: "attention" | "danger" } | null {
  if (record.status === "voided") return { label: "Voided", tone: "danger" };
  if (record.overall_result === "fail")
    return { label: "Fail", tone: "danger" };
  if (record.overall_result === "warn")
    return { label: "Warning", tone: "attention" };
  return null;
}

const units: ReadonlyArray<readonly [RegExp, string]> = [
  [/_c$/, " °C"],
  [/_g$/, " g"],
  [/_minutes$/, " min"],
  [/_days$/, " days"],
  [/_ntu$/, " NTU"],
  [/_mg_l$/, " mg/L"],
];

function isInstant(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value);
}

function isDay(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

// One stored value as a person reads it: units, local times and words.
export function formatValue(
  key: string,
  value: unknown,
  timezone: string,
): string {
  if (typeof value === "number") {
    const suffix = units.find(([pattern]) => pattern.test(key))?.[1] ?? "";
    return `${displayRecordValue(value)}${suffix}`;
  }
  if (typeof value === "string") {
    if (isInstant(value)) return dateTimeLabel(value, timezone);
    if (isDay(value)) return dateLabel(value, "medium");
  }
  return displayRecordValue(value);
}

export function recordFieldLabel(code: string, key: string): string {
  const form = typedLogForms[code];
  const field = [
    ...(form?.fields ?? []),
    ...(form?.repeatable?.fields ?? []),
  ].find((entry) => entry.key === key);
  return field?.label ?? humanLabel(key);
}

export interface DetailItem {
  badge?: { label: string; tone: "danger" | "positive" };
  fields: Array<{ label: string; text: string }>;
  title?: string;
}

export interface DetailRow {
  items?: DetailItem[];
  key: string;
  label: string;
  text?: string;
}

const hiddenKeys = new Set([
  "id",
  "supplier_product_id",
  "target_min_c",
  "target_max_c",
]);

function empty(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function scalar(
  code: string,
  key: string,
  value: unknown,
  names: RecordNames,
  timezone: string,
): string | null {
  if (key.endsWith("_id")) {
    const name = named(names, value);
    return name ?? null;
  }
  void code;
  return formatValue(key, value, timezone);
}

function itemFields(
  code: string,
  item: Record<string, unknown>,
  names: RecordNames,
  timezone: string,
  skip: ReadonlySet<string>,
): DetailItem["fields"] {
  const fields: DetailItem["fields"] = [];
  for (const [key, value] of Object.entries(item)) {
    if (hiddenKeys.has(key) || skip.has(key) || empty(value)) continue;
    if (typeof value === "object") continue;
    const shown = scalar(code, key, value, names, timezone);
    if (shown !== null)
      fields.push({ label: recordFieldLabel(code, key), text: shown });
  }
  return fields;
}

function expectedRange(values: Record<string, unknown>): string | null {
  const min = values.target_min_c;
  const max = values.target_max_c;
  const low = typeof min === "number" ? formatValue("c", min, "") : null;
  const high = typeof max === "number" ? formatValue("c", max, "") : null;
  if (low && high) return `${low} to ${high} °C`;
  if (high) return `Up to ${high} °C`;
  if (low) return `At least ${low} °C`;
  return null;
}

// What the drawer shows: names instead of ids, words instead of codes.
export function detailRows(
  record: RecordEntry,
  names: RecordNames,
  timezone: string,
): DetailRow[] {
  const code = record.record_type_code;
  const values = valuesOf(record);
  const rows: DetailRow[] = [];
  for (const [key, value] of Object.entries(values)) {
    if (hiddenKeys.has(key) || empty(value)) continue;
    const label = recordFieldLabel(code, key);
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      if (value.every((entry) => typeof entry !== "object" || entry === null)) {
        rows.push({
          key,
          label,
          text: value.map((entry) => displayRecordValue(entry)).join(", "),
        });
        continue;
      }
      const items = (value as Array<Record<string, unknown>>).map(
        (entry, index): DetailItem => {
          const title =
            text(entry.food_description) ??
            `${label.replace(/s$/, "")} ${index + 1}`;
          return {
            ...(typeof entry.accepted === "boolean"
              ? {
                  badge: entry.accepted
                    ? { label: "Accepted", tone: "positive" as const }
                    : { label: "Rejected", tone: "danger" as const },
                }
              : {}),
            fields: itemFields(
              code,
              entry,
              names,
              timezone,
              new Set(["food_description", "accepted"]),
            ),
            title,
          };
        },
      );
      rows.push({ items, key, label });
      continue;
    }
    if (typeof value === "object") {
      const fields = itemFields(
        code,
        value as Record<string, unknown>,
        names,
        timezone,
        new Set(),
      );
      if (fields.length > 0) rows.push({ items: [{ fields }], key, label });
      continue;
    }
    const shown = scalar(code, key, value, names, timezone);
    if (shown !== null) rows.push({ key, label, text: shown });
  }
  const range = expectedRange(values);
  if (range)
    rows.push({ key: "expected_range", label: "Expected range", text: range });
  return rows;
}

export interface RecordDay {
  date: string;
  items: RecordEntry[];
}

export function localDate(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: timezone,
    year: "numeric",
  }).format(new Date(instant));
}

// Keeps the incoming order; a new group starts whenever the local day changes.
export function groupRecordsByDay(
  records: readonly RecordEntry[],
  timezone: string,
): RecordDay[] {
  const days: RecordDay[] = [];
  for (const record of records) {
    const date = localDate(record.occurred_at, timezone);
    const last = days[days.length - 1];
    if (last && last.date === date) last.items.push(record);
    else days.push({ date, items: [record] });
  }
  return days;
}

const recordLevelLabels: Readonly<Record<string, string>> = {
  note: "Note",
  occurred_at: "When",
  performed_by_staff_id: "Performed by",
};

export interface ChangeLine {
  after: string;
  before: string;
  label: string;
}

function changeValue(
  code: string,
  key: string,
  value: unknown,
  names: RecordNames,
  timezone: string,
): string {
  if (empty(value)) return "None";
  if (typeof value === "object") return displayRecordValue(value);
  return scalar(code, key, value, names, timezone) ?? "Updated";
}

// A change as "Temperature °C: 6.8 °C to 4.8 °C", never raw stored JSON.
export function changeLines(
  revision: Pick<
    RecordRevision,
    "changed_fields" | "new_values" | "previous_values"
  >,
  code: string,
  names: RecordNames,
  timezone: string,
): ChangeLine[] {
  return revision.changed_fields.map((key) => ({
    after: changeValue(code, key, revision.new_values[key], names, timezone),
    before: changeValue(
      code,
      key,
      revision.previous_values[key],
      names,
      timezone,
    ),
    label: recordLevelLabels[key] ?? recordFieldLabel(code, key),
  }));
}
